import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { langFromPath } from '../i18n';

// The panel loads when first opened, to keep the main bundle small
const ChatPanel = lazy(() => import('./ChatPanel'));

const STATUS_KEY = 'akka_chat_available';
const OPEN_LABEL = { tr: 'Soru sorun', en: 'Ask a question', de: 'Frage stellen' };

// The server decides from the visitor's country (netlify/functions/chat), asked once per visit
async function isAvailable() {
    try {
        const saved = sessionStorage.getItem(STATUS_KEY);
        if (saved) return saved === 'yes';
    } catch {
        // Storage can be blocked; the check below still works
    }

    let available;
    try {
        const response = await fetch('/api/chat');
        available = (await response.json()).available === true;
    } catch {
        return false;
    }

    try {
        sessionStorage.setItem(STATUS_KEY, available ? 'yes' : 'no');
    } catch {
        // Asked again on the next page load instead
    }
    return available;
}

const ChatWidget = () => {
    const { pathname } = useLocation();
    const lang = langFromPath(pathname);
    const isAdmin = pathname === '/admin' || pathname.startsWith('/admin/');
    const [available, setAvailable] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);
    const buttonRef = useRef(null);
    const wasOpen = useRef(false);

    useEffect(() => {
        if (isAdmin) return undefined;
        let active = true;
        isAvailable().then((result) => {
            if (active) setAvailable(result);
        });
        return () => {
            active = false;
        };
    }, [isAdmin]);

    // Keyboard users land back on the button after closing the panel
    useEffect(() => {
        if (wasOpen.current && !open && buttonRef.current) buttonRef.current.focus();
        wasOpen.current = open;
    }, [open]);

    if (isAdmin || !available) return null;

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                aria-expanded={open}
                onClick={() => {
                    setLoaded(true);
                    setOpen(true);
                }}
                className={`${open ? 'hidden' : 'flex'} fixed bottom-4 right-4 z-40 items-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full shadow-lg transition duration-300`}
            >
                <svg aria-hidden="true" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h8M8 14h5m-7 6l3.5-3H18a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v9a2 2 0 002 2v3z" />
                </svg>
                {OPEN_LABEL[lang]}
            </button>
            {loaded && (
                <Suspense fallback={null}>
                    <ChatPanel lang={lang} open={open} onClose={() => setOpen(false)} />
                </Suspense>
            )}
        </>
    );
};

export default ChatWidget;
